import { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { TextField, BodyText, MaterialSymbol, withStyles, Card, CircleButton } from 'vopay';
import { Title, Button } from 'UI';
import type { OpenDialogProps } from '@/providers/Dialog.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import usePostCreateAccountUser from '@/hooks/useApi/usePostCreateAccountUser';
import newUserIcon from './newUser.svg';
import styles from './AddUserDialog.module.scss';

const cn = withStyles(styles);

export type AddUserDialogResult = { status: 'success' } | { status: 'error'; errorMessage: string };

type AddUserDialogProps = OpenDialogProps<AddUserDialogResult> & {
  bearer: string;
};

const AddUserDialog: FunctionComponent<AddUserDialogProps> = ({ onConfirm, onCancel, bearer }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.AddUserDialog' });
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const emailSchema = Yup.string().required(t('emailRequired')).email(t('emailInvalid'));

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [createUserResponse, createAccountUser] = usePostCreateAccountUser({ bearer });

  const validateEmail = async (value: string) => {
    try {
      await emailSchema.validate(value);
      setEmailError(null);
    } catch (err) {
      if (err instanceof Yup.ValidationError) setEmailError(err.message);
    }
  };

  const isEmailValid = (() => {
    try {
      emailSchema.validateSync(email);
      return true;
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    if (!hasSubmitted) return;
    const { Success, ErrorMessage } = createUserResponse;
    if (!Success && !ErrorMessage) return;

    setHasSubmitted(false);

    if (Success) {
      onConfirm({ status: 'success' });
    } else {
      onConfirm({ status: 'error', errorMessage: ErrorMessage });
    }
  }, [createUserResponse]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (touched) validateEmail(value);
  };

  const handleBlur = () => {
    setTouched(true);
    validateEmail(email);
  };

  const handleConfirm = () => {
    setHasSubmitted(true);
    createAccountUser({ EmailAddress: email, SendEmail: true });
  };

  return (
    <Card hoverEffect={false} className={cn('dialog')}>
      <div className={cn('closeButton')}>
        <CircleButton colorScheme="neutral" icon="close" size="large" onClick={onCancel} />
      </div>
      <img className={cn('image')} src={newUserIcon} alt={t('iconAlt', { defaultValue: 'Add New User' })} />
      <div className={cn('header')}>
        <Title level="3">{t('title')}</Title>
        <BodyText className={cn('greyText')}>{t('description')}</BodyText>
      </div>

      <TextField
        name="email"
        placeholder={t('emailPlaceholder')}
        value={email}
        onChange={handleChange}
        onBlur={handleBlur}
        variant="outlineShadow"
        elementLeft={
          <MaterialSymbol className={cn('emailIcon')} fillAmount={1} icon="email" size="20px" color="#2d2d2d" />
        }
        error={touched ? emailError : null}
      />

      <Button
        id="add-user-confirm"
        colorScheme="primary"
        size="small"
        disabled={!isEmailValid || isRoleViewOnly}
        onClick={handleConfirm}
      >
        {t('confirmButton')}
      </Button>
    </Card>
  );
};

export default AddUserDialog;
